import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Lock, 
  Loader2, 
  Check, 
  Building2, 
  MessageSquare, 
  Wrench,
  ArrowRight,
  ArrowLeft,
  CreditCard,
  Shield,
  FileText,
  Headphones,
  ChevronRight,
  Star,
  Globe,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import WowCXLogo from '@/components/WowCXLogo';

// Use current origin for API calls
const API = `${window.location.origin}/api`;

// Base pages included in build
const BASE_PAGES = 4;

// One-time build pricing
const BUILD_PRICING = {
  basePages: BASE_PAGES,
  baseBuildUSD: 200,
  baseBuildINR: 15000,
  additionalPageUSD: 35,
  additionalPageINR: 2500
};

const PLANS = {
  diy: {
    id: 'diy_with_support',
    name: 'DIY with Support',
    tagline: 'Every Wow!CX account comes with free support and access to our knowledgebase.',
    priceUSD: 2.95,
    priceINR: 239.95,
    normalPriceUSD: 10.99,
    normalPriceINR: 879.99,
    billing: 'Payable yearly',
    features: {
      hosting: ['10 GB SSD Storage', 'Free CDN', '1 Website', 'Free Domain 1st Year'],
      website: ['WP Themes', 'WP Builders', 'WP Plugins'],
      security: ['Free SSL – 1st Year'],
      marketing: ['Yoast SEO Free']
    },
    expectResponse: false,
    includesBuild: false
  },
  guidance: {
    id: 'design_guidance',
    name: 'Design Guidance',
    tagline: 'Get WordPress website guidance every step of the way from our experts.',
    priceUSD: 9.95,
    priceINR: 799.95,
    normalPriceUSD: 23.99,
    normalPriceINR: 1919.99,
    billing: 'Payable yearly',
    features: {
      hosting: ['10 GB SSD Storage', 'Free CDN', '1 Website', 'Free Domain 1st Year'],
      website: ['WP Themes', 'WP Builders', 'WP Plugins'],
      security: ['Free SSL – 1st Year'],
      marketing: ['Yoast SEO Free'],
      expertise: ['On-Demand Ticket Support', 'Account Access Assistance', 'Website Back Up Assistance', 'Design Guidance']
    },
    expectResponse: true,
    popular: true,
    includesBuild: false
  },
  build: {
    id: 'we_build_it_for_you',
    name: 'We Build It 4 You',
    tagline: 'Let our expert design team create and maintain your entire website for you.',
    priceUSD: 19.95,
    priceINR: 1599.95,
    normalPriceUSD: 49.99,
    normalPriceINR: 3999.99,
    billing: 'Payable yearly',
    features: {
      hosting: ['10 GB SSD Storage', 'Free CDN', '1 Website', 'Free Domain 1st Year'],
      website: ['WP Themes', 'WP Builders', 'WP Plugins'],
      security: ['Free SSL – 1st Year'],
      marketing: ['Yoast SEO Free'],
      expertise: ['On-Demand Ticket Support', 'Account Access Assistance', 'Website Back Up Assistance', 'Design Guidance'],
      designBuild: [`Full-service Website Build (${BASE_PAGES} pages)`, 'SEO Services']
    },
    expectResponse: true,
    includesBuild: true
  },
  save_only: {
    id: 'save_only',
    name: 'Save Design Only',
    tagline: 'Keep your design safe in your account for future reference.',
    priceUSD: 0,
    priceINR: 0,
    normalPriceUSD: 0,
    normalPriceINR: 0,
    billing: 'Free',
    features: {
      included: ['Design saved in your account', 'Access anytime', 'Download design files', 'Share with others']
    },
    expectResponse: false,
    includesBuild: false,
    isFree: true
  }
};

const OPTIONS = [
  {
    id: 'build',
    title: 'Submit to Wow!CX for website development',
    description: 'Our expert team will build your complete website based on this design.',
    icon: <Building2 className="w-6 h-6" />,
    planKey: 'build',
    highlight: true
  },
  {
    id: 'guidance',
    title: 'Submit to Wow!CX for design review and guidance',
    description: 'Get expert feedback and guidance on your design before you build it yourself.',
    icon: <MessageSquare className="w-6 h-6" />,
    planKey: 'guidance'
  },
  {
    id: 'diy',
    title: 'Host with Wow!CX and build it yourself',
    description: 'Lock in your design, get our hosting package with support, and build it yourself.',
    icon: <Wrench className="w-6 h-6" />,
    planKey: 'diy'
  },
  {
    id: 'save_only',
    title: 'Just save the design',
    description: 'Freeze and save your design to your account. No hosting or development services.',
    icon: <Lock className="w-6 h-6" />,
    planKey: 'save_only',
    subtle: true
  }
];

const PlaceOrderPage = () => {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const designId = searchParams.get('design');
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [selectedOption, setSelectedOption] = useState(null);
  const [currency, setCurrency] = useState('INR');
  const [freezing, setFreezing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [design, setDesign] = useState(null);

  const selectedPlan = selectedOption ? PLANS[selectedOption] : null;
  const pageCount = design?.pages?.length || 4;
  const additionalPages = Math.max(0, pageCount - BASE_PAGES);

  useEffect(() => {
    fetchProjectData();
  }, [projectId, designId]);

  const fetchProjectData = async () => {
    try {
      const [projectRes, designsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, { withCredentials: true }),
        axios.get(`${API}/projects/${projectId}/designs`, { withCredentials: true })
      ]);
      
      setProject(projectRes.data);
      
      if (designsRes.data.length > 0) {
        const targetDesign = designId 
          ? designsRes.data.find(d => d.design_id === designId) 
          : designsRes.data[0];
        setDesign(targetDesign || designsRes.data[0]);
      }
    } catch (error) {
      console.error('Failed to load project:', error);
      toast.error('Failed to load project');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getPricingBreakdown = () => {
    if (!selectedPlan) return null;
    
    const currencySymbol = currency === 'INR' ? '₹' : '$';
    const monthlyPrice = currency === 'INR' ? selectedPlan.priceINR : selectedPlan.priceUSD;
    const yearlyHosting = monthlyPrice * 12;
    
    let breakdown = {
      hosting: {
        label: `${selectedPlan.name} (Annual)`,
        amount: yearlyHosting,
        description: `${currencySymbol}${monthlyPrice}/mo × 12 months`
      },
      items: []
    };
    
    if (selectedPlan.includesBuild) {
      const baseBuildFee = currency === 'INR' ? BUILD_PRICING.baseBuildINR : BUILD_PRICING.baseBuildUSD;
      const additionalPageFee = currency === 'INR' ? BUILD_PRICING.additionalPageINR : BUILD_PRICING.additionalPageUSD;
      
      breakdown.items.push({
        label: `Website Build (${BASE_PAGES} pages)`,
        amount: baseBuildFee,
        description: 'One-time build fee',
        oneTime: true
      });
      
      if (additionalPages > 0) {
        breakdown.items.push({
          label: `Additional Pages (${additionalPages})`,
          amount: additionalPageFee * additionalPages,
          description: `${currencySymbol}${additionalPageFee} × ${additionalPages} pages`,
          oneTime: true
        });
      }
    }
    
    const oneTimeTotal = breakdown.items.reduce((sum, item) => sum + item.amount, 0);
    breakdown.total = yearlyHosting + oneTimeTotal;
    breakdown.oneTimeTotal = oneTimeTotal;
    breakdown.recurringTotal = yearlyHosting;
    
    return breakdown;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (document.getElementById('razorpay-script')) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.id = 'razorpay-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePlaceOrder = async () => {
    if (!selectedOption) return;
    
    const pricing = getPricingBreakdown();
    
    setFreezing(true);
    try {
      await axios.post(`${API}/projects/${projectId}/freeze`, {
        design_id: design?.design_id,
        plan_id: selectedPlan.id,
        plan_name: selectedPlan.name,
        expects_response: selectedPlan.expectResponse,
        page_count: pageCount,
        additional_pages: additionalPages,
        pricing_breakdown: pricing
      }, { withCredentials: true });

      // Handle free options (save_only and diy with no payment)
      if (selectedOption === 'save_only') {
        toast.success('Design saved successfully! Access it anytime from your dashboard.');
        navigate('/dashboard');
        return;
      }

      if (selectedOption === 'diy') {
        toast.success('Design frozen successfully! You can now build it yourself.');
        navigate('/dashboard');
        return;
      }

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway. Please try again.');
        setFreezing(false);
        return;
      }

      const totalAmount = Math.round(pricing.total * 100);

      const orderResponse = await axios.post(`${API}/projects/${projectId}/freeze/create-order`, {
        plan_id: selectedPlan.id,
        amount: totalAmount,
        currency: currency,
        page_count: pageCount,
        additional_pages: additionalPages
      }, { withCredentials: true });

      const { order_id, key_id } = orderResponse.data;

      const currencySymbol = currency === 'INR' ? '₹' : '$';
      let description = `${selectedPlan.name} - Annual Plan`;
      if (selectedPlan.includesBuild) {
        description = `${selectedPlan.name} + Website Build (${pageCount} pages)`;
      }

      const options = {
        key: key_id,
        amount: totalAmount,
        currency: currency,
        name: 'Wow!CX',
        description: description,
        order_id: order_id,
        handler: async function (response) {
          try {
            await axios.post(`${API}/projects/${projectId}/freeze/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            }, { withCredentials: true });

            toast.success('Payment successful! Your design has been submitted for review.');
            navigate('/dashboard');
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {},
        notes: {
          project_id: projectId,
          plan_id: selectedPlan.id,
          page_count: pageCount
        },
        theme: {
          color: '#4F46E5'
        },
        modal: {
          ondismiss: function() {
            setFreezing(false);
            toast.info('Payment cancelled. Your design has been saved as pending.');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order');
      setFreezing(false);
    }
  };

  const renderPlanCard = (planKey, isSelected) => {
    const plan = PLANS[planKey];
    const isLocked = selectedOption && planKey !== selectedOption;
    const price = currency === 'INR' ? plan.priceINR : plan.priceUSD;
    const normalPrice = currency === 'INR' ? plan.normalPriceINR : plan.normalPriceUSD;
    const currencySymbol = currency === 'INR' ? '₹' : '$';

    return (
      <div 
        key={planKey}
        onClick={() => !isLocked && setSelectedOption(planKey)}
        className={`relative rounded-xl border-2 p-6 transition-all cursor-pointer ${
          isSelected 
            ? 'border-indigo-500 bg-white shadow-lg ring-2 ring-indigo-100' 
            : isLocked 
              ? 'border-zinc-200 bg-zinc-50 opacity-50 cursor-not-allowed' 
              : 'border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-md'
        }`}
      >
        {plan.popular && !isLocked && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-indigo-600 text-white text-xs font-medium rounded-full">
            Popular
          </div>
        )}

        <div className="flex items-start gap-3 mb-4">
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 ${
            isSelected 
              ? 'border-indigo-600 bg-indigo-600' 
              : isLocked 
                ? 'border-zinc-300 bg-zinc-200' 
                : 'border-zinc-300'
          }`}>
            {isSelected && <Check className="w-3 h-3 text-white" />}
            {isLocked && <Lock className="w-3 h-3 text-zinc-400" />}
          </div>
          <div className="flex-1">
            <h3 className={`font-bold text-lg ${isLocked ? 'text-zinc-400' : 'text-zinc-900'}`}>
              {plan.name}
            </h3>
            <p className={`text-sm ${isLocked ? 'text-zinc-400' : 'text-zinc-600'}`}>
              {plan.tagline}
            </p>
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-baseline gap-1">
            <span className={`text-3xl font-bold ${isLocked ? 'text-zinc-400' : 'text-zinc-900'}`}>
              {currencySymbol}{price}
            </span>
            <span className={`text-sm ${isLocked ? 'text-zinc-400' : 'text-zinc-500'}`}>/mo*</span>
          </div>
          <div className={`text-sm ${isLocked ? 'text-zinc-400' : 'text-zinc-500'}`}>
            <span className="line-through">Normally {currencySymbol}{normalPrice}</span>
          </div>
          <div className={`text-xs ${isLocked ? 'text-zinc-400' : 'text-zinc-500'}`}>
            {plan.billing}
          </div>
        </div>

        <div className="space-y-3 text-sm">
          {Object.entries(plan.features).map(([category, features]) => (
            <div key={category}>
              <div className={`font-semibold text-xs uppercase tracking-wider mb-1 ${
                isLocked ? 'text-zinc-400' : 'text-zinc-500'
              }`}>
                {category.replace(/([A-Z])/g, ' $1').trim()}
              </div>
              <ul className="space-y-1">
                {features.map((feature, idx) => (
                  <li key={idx} className={`flex items-center gap-2 ${isLocked ? 'text-zinc-400' : 'text-zinc-700'}`}>
                    <Check className={`w-3.5 h-3.5 ${isLocked ? 'text-zinc-400' : 'text-green-500'}`} />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {plan.expectResponse && !isLocked && (
          <div className="mt-4 p-3 bg-indigo-50 rounded-lg">
            <p className="text-xs text-indigo-700 flex items-center gap-2">
              <Headphones className="w-4 h-4" />
              You will receive design feedback from our experts
            </p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 overflow-x-hidden">
      {/* Header */}
      <header className="px-3 sm:px-6 md:px-12 lg:px-24 py-3 md:py-6 bg-white border-b border-zinc-200">
        <div className="max-w-6xl mx-auto flex justify-between items-center gap-2 overflow-hidden">
          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
            <WowCXLogo size="sm" className="flex-shrink-0" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/preview/${projectId}${designId ? `?design=${designId}` : ''}`)}
              className="hidden sm:flex"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/preview/${projectId}${designId ? `?design=${designId}` : ''}`)}
              className="sm:hidden h-8 w-8"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </div>
          <div className="text-right min-w-0 overflow-hidden">
            <h1 className="text-xs sm:text-sm md:text-lg font-bold text-zinc-900 truncate max-w-[120px] sm:max-w-[200px] md:max-w-none" style={{fontFamily: 'Outfit'}}>
              {project?.business_info?.business_name || 'Your Project'}
            </h1>
            <p className="text-xs text-zinc-500">{pageCount} pages</p>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="px-3 sm:px-6 md:px-12 lg:px-24 py-2 md:py-4 bg-zinc-100 border-b border-zinc-200">
        <div className="max-w-6xl mx-auto flex items-center gap-1.5 md:gap-2 text-xs md:text-sm">
          <span className={step >= 1 ? 'text-indigo-600 font-medium' : 'text-zinc-400'}>Choose Plan</span>
          <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-zinc-400 flex-shrink-0" />
          <span className={step >= 2 ? 'text-indigo-600 font-medium' : 'text-zinc-400'}>Review & Pay</span>
        </div>
      </div>

      {/* Main Content - Scrollable */}
      <div className="px-3 sm:px-6 md:px-12 lg:px-24 py-4 md:py-12">
        <div className="max-w-6xl mx-auto pb-8">
          {step === 1 ? (
            /* Step 1: Choose Plan - With Light Colors */
            <div>
              {/* Header with subtle gradient background */}
              <div className="text-center mb-6 md:mb-10 p-4 md:p-8 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-2xl md:rounded-3xl border border-indigo-100">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-white/80 backdrop-blur rounded-full text-indigo-700 text-xs md:text-sm font-medium mb-3 md:mb-4 shadow-sm">
                  <Sparkles className="w-3 h-3 md:w-4 md:h-4" />
                  Your design is ready!
                </div>
                <h2 className="text-xl md:text-3xl font-bold text-zinc-900 mb-2 md:mb-3" style={{fontFamily: 'Outfit'}}>
                  How would you like to proceed?
                </h2>
                <p className="text-zinc-600 max-w-2xl mx-auto text-xs md:text-base">
                  Choose the option that best fits your needs
                </p>
              </div>

              <div className="space-y-2 md:space-y-4 max-w-2xl mx-auto mb-6 md:mb-8">
                {OPTIONS.map((option, idx) => {
                  const colors = [
                    { bg: 'from-indigo-500 to-purple-500', light: 'bg-indigo-50 border-indigo-200', selected: 'border-indigo-500 bg-indigo-50 shadow-indigo-100' },
                    { bg: 'from-blue-500 to-cyan-500', light: 'bg-blue-50 border-blue-200', selected: 'border-blue-500 bg-blue-50 shadow-blue-100' },
                    { bg: 'from-emerald-500 to-teal-500', light: 'bg-emerald-50 border-emerald-200', selected: 'border-emerald-500 bg-emerald-50 shadow-emerald-100' },
                    { bg: 'from-zinc-400 to-zinc-500', light: 'bg-zinc-50 border-zinc-200', selected: 'border-zinc-400 bg-zinc-50 shadow-zinc-100' }
                  ];
                  const color = colors[idx] || colors[0];
                  const isSelected = selectedOption === option.planKey;
                  
                  return (
                    <button
                      key={option.id}
                      onClick={() => setSelectedOption(option.planKey)}
                      className={`w-full p-3 md:p-5 rounded-xl border-2 text-left transition-all ${
                        isSelected
                          ? `${color.selected} shadow-lg`
                          : 'border-zinc-200 hover:border-zinc-300 hover:shadow-md bg-white'
                      }`}
                      data-testid={`freeze-option-${option.id}`}
                    >
                      <div className="flex items-start gap-2.5 md:gap-4">
                        <div className={`w-9 h-9 md:w-12 md:h-12 rounded-lg md:rounded-xl flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? `bg-gradient-to-br ${color.bg} text-white shadow-lg`
                            : 'bg-zinc-100 text-zinc-600'
                        }`}>
                          {React.cloneElement(option.icon, { className: 'w-4 h-4 md:w-6 md:h-6' })}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className={`font-semibold text-xs md:text-base leading-tight ${
                              isSelected ? 'text-zinc-900' : 'text-zinc-900'
                            }`}>
                              {option.title}
                            </h3>
                            <div className={`w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              isSelected
                                ? `border-current bg-gradient-to-br ${color.bg}`
                                : 'border-zinc-300'
                            }`}>
                              {isSelected && (
                                <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" />
                              )}
                            </div>
                          </div>
                          <p className="text-[10px] md:text-sm text-zinc-500 mt-0.5 md:mt-1 leading-tight">{option.description}</p>
                          {option.highlight && (
                            <span className="inline-block mt-1.5 md:mt-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] md:text-xs font-medium rounded-full">
                              Most Popular
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex justify-center">
                <Button 
                  onClick={() => setStep(2)}
                  disabled={!selectedOption}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 md:px-8 h-10 md:h-12 text-sm md:text-base shadow-lg hover:shadow-xl transition-all hover:scale-105"
                  data-testid="continue-to-review-btn"
                >
                  Continue to Review <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          ) : (
            /* Step 2: Review & Pay - Two Column Layout */
            <div>
              {/* Header */}
              <div className="text-center mb-4 md:mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full text-indigo-700 text-xs md:text-sm font-medium mb-3 md:mb-4">
                  <Sparkles className="w-3 h-3 md:w-4 md:h-4" />
                  Almost there!
                </div>
                <h2 className="text-xl md:text-4xl font-bold text-zinc-900 mb-2 md:mb-3" style={{fontFamily: 'Outfit'}}>
                  Complete Your Order
                </h2>
                <p className="text-zinc-600 text-sm md:text-lg">
                  You&apos;ve selected <span className="font-semibold text-indigo-600">{selectedPlan?.name}</span>
                </p>
              </div>

              {/* Currency Toggle */}
              {!selectedPlan?.isFree && (
                <div className="flex justify-center mb-6 md:mb-10">
                  <div className="inline-flex rounded-full border-2 border-zinc-200 p-1 bg-white shadow-sm">
                    <button
                      onClick={() => setCurrency('USD')}
                      className={`px-4 md:px-6 py-2 md:py-2.5 rounded-full text-sm font-semibold transition-all ${
                        currency === 'USD' 
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg' 
                          : 'text-zinc-600 hover:text-zinc-900'
                      }`}
                    >
                      🇺🇸 USD
                    </button>
                    <button
                      onClick={() => setCurrency('INR')}
                      className={`px-4 md:px-6 py-2 md:py-2.5 rounded-full text-sm font-semibold transition-all ${
                        currency === 'INR' 
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg' 
                          : 'text-zinc-600 hover:text-zinc-900'
                      }`}
                    >
                      🇮🇳 INR
                    </button>
                  </div>
                </div>
              )}

              {/* Two Column Layout: Features + Price Card */}
              {selectedPlan && !selectedPlan.isFree && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8">
                  {/* Left: Plan Features */}
                  <div className="bg-white rounded-2xl border border-zinc-200 p-6 md:p-8 order-2 lg:order-1">
                    <h3 className="text-lg font-bold text-zinc-900 mb-6 flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-500" />
                      What&apos;s Included
                    </h3>
                    
                    <div className="space-y-6">
                      {Object.entries(selectedPlan.features).map(([category, features]) => (
                        <div key={category}>
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-3">
                            {category.replace(/([A-Z])/g, ' $1').trim()}
                          </h4>
                          <ul className="space-y-2">
                            {features.map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-3 text-sm text-zinc-700">
                                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>

                    {selectedPlan.expectResponse && (
                      <div className="mt-6 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                        <p className="text-sm text-indigo-700 flex items-center gap-2">
                          <Headphones className="w-4 h-4" />
                          You&apos;ll receive expert feedback on your design
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Right: Price Card + Order Summary */}
                  <div className="order-1 lg:order-2 space-y-4">
                    {/* Price Card */}
                    <div className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 rounded-2xl md:rounded-3xl p-6 md:p-8 text-white overflow-hidden shadow-2xl">
                      {/* Background decoration */}
                      <div className="absolute top-0 right-0 w-48 md:w-64 h-48 md:h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                      <div className="absolute bottom-0 left-0 w-32 md:w-48 h-32 md:h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                      
                      <div className="relative z-10">
                        {selectedPlan.popular && (
                          <div className="inline-flex items-center gap-1 px-3 py-1 bg-amber-400 text-amber-900 text-xs font-bold rounded-full mb-4">
                            <Star className="w-3 h-3" /> MOST POPULAR
                          </div>
                        )}
                        
                        <h3 className="text-xl md:text-2xl font-bold mb-2">{selectedPlan.name}</h3>
                        <p className="text-indigo-100 text-sm md:text-base mb-6">{selectedPlan.tagline}</p>
                        
                        <div className="flex items-baseline gap-2 mb-4">
                          <span className="text-4xl md:text-5xl font-bold">
                            {currency === 'INR' ? '₹' : '$'}{(currency === 'INR' ? selectedPlan.priceINR : selectedPlan.priceUSD).toFixed(2)}
                          </span>
                          <span className="text-indigo-200 text-base md:text-lg">/month</span>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-2 md:gap-4 text-sm text-indigo-100">
                          <span className="line-through opacity-75">
                            Normally {currency === 'INR' ? '₹' : '$'}{(currency === 'INR' ? selectedPlan.normalPriceINR : selectedPlan.normalPriceUSD).toFixed(2)}/mo
                          </span>
                          <span className="px-2 py-1 bg-green-400 text-green-900 rounded-full text-xs font-bold">
                            SAVE {Math.round((1 - (currency === 'INR' ? selectedPlan.priceINR : selectedPlan.priceUSD) / (currency === 'INR' ? selectedPlan.normalPriceINR : selectedPlan.normalPriceUSD)) * 100)}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Order Summary - Compact */}
                    <div className="bg-white rounded-2xl border border-zinc-200 shadow-lg overflow-hidden" data-testid="pricing-breakdown">
                      <div className="bg-gradient-to-r from-zinc-50 to-zinc-100 px-4 py-3 border-b border-zinc-200">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-indigo-600" />
                          <h4 className="font-bold text-zinc-900 text-sm">Order Summary</h4>
                        </div>
                      </div>
                      
                      <div className="p-4">
                        {(() => {
                          const pricing = getPricingBreakdown();
                          const currencySymbol = currency === 'INR' ? '₹' : '$';
                          
                          const formatPrice = (amount) => {
                            return amount.toLocaleString(currency === 'INR' ? 'en-IN' : 'en-US', { 
                              minimumFractionDigits: 2, 
                              maximumFractionDigits: 2 
                            });
                          };
                          
                          return (
                            <div className="space-y-3 text-sm">
                              {/* Hosting */}
                              <div className="flex justify-between items-center">
                                <span className="text-zinc-600">{pricing.hosting.label}</span>
                                <span className="font-medium text-zinc-900">
                                  {currencySymbol}{formatPrice(pricing.hosting.amount)}
                                </span>
                              </div>
                              
                              {/* Build items */}
                              {pricing.items.map((item, idx) => (
                                <div key={idx} className="flex justify-between items-center">
                                  <span className="text-zinc-600">{item.label}</span>
                                  <span className="font-medium text-zinc-900">
                                    {currencySymbol}{formatPrice(item.amount)}
                                  </span>
                                </div>
                              ))}
                              
                              {/* Total */}
                              <div className="flex justify-between items-center pt-3 mt-2 border-t-2 border-dashed border-zinc-200">
                                <span className="font-bold text-zinc-900">Total Due Today</span>
                                <span className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                  {currencySymbol}{formatPrice(pricing.total)}
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Trust Indicators - Inside Right Column */}
                    <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 rounded-full">
                        <Shield className="w-3.5 h-3.5 text-green-600" />
                        <span className="text-xs font-medium text-green-700">30-Day Money-Back</span>
                      </div>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-full">
                        <CreditCard className="w-3.5 h-3.5 text-blue-600" />
                        <span className="text-xs font-medium text-blue-700">Secure Payment</span>
                      </div>
                    </div>

                    {/* Disclaimer */}
                    <p className="text-xs text-zinc-400 text-center mt-3">
                      * Promotional pricing for first term only. Renews at regular rate.
                    </p>

                    {/* CTA Buttons - Inside Right Column */}
                    <div className="flex flex-col gap-2 mt-4">
                      <Button 
                        onClick={handlePlaceOrder}
                        disabled={freezing}
                        className="w-full h-12 text-base font-semibold shadow-lg transition-all hover:scale-[1.02] bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                        data-testid="place-order-btn"
                      >
                        {freezing ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-5 h-5 mr-2" />
                            Pay {currency === 'INR' ? '₹' : '$'}{getPricingBreakdown()?.total.toFixed(2)}
                          </>
                        )}
                      </Button>
                      <Button variant="ghost" onClick={() => setStep(1)} className="w-full text-zinc-500 hover:text-zinc-700">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Change Plan
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Save Only - Simple Card */}
              {selectedPlan?.isFree && (
                <div className="max-w-2xl mx-auto mb-8">
                  <div className="bg-zinc-100 rounded-2xl md:rounded-3xl p-6 md:p-8 border-2 border-zinc-200">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-zinc-200 flex items-center justify-center">
                        <Lock className="w-6 h-6 md:w-7 md:h-7 text-zinc-600" />
                      </div>
                      <div>
                        <h3 className="text-lg md:text-xl font-bold text-zinc-900">{selectedPlan.name}</h3>
                        <p className="text-zinc-600 text-sm">{selectedPlan.tagline}</p>
                      </div>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-zinc-900 mb-4">Free</div>
                    <ul className="space-y-2">
                      {selectedPlan.features.included.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-zinc-700 text-sm">
                          <Check className="w-4 h-4 text-green-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {/* CTA for Save Only */}
                    <div className="mt-6 flex flex-col gap-2">
                      <Button 
                        onClick={handlePlaceOrder}
                        disabled={freezing}
                        className="w-full h-12 text-base font-semibold bg-zinc-800 hover:bg-zinc-900 text-white"
                        data-testid="place-order-btn"
                      >
                        {freezing ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Lock className="w-5 h-5 mr-2" />
                            Save Design
                          </>
                        )}
                      </Button>
                      <Button variant="ghost" onClick={() => setStep(1)} className="w-full text-zinc-500 hover:text-zinc-700">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Change Plan
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaceOrderPage;
